import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthService, ForgotPasswordRequest } from '../../services/authService';
import { Button } from '../common/Button';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

export const ForgotPassword: React.FC = () => {
  const [formData, setFormData] = useState<ForgotPasswordRequest>({
    customerNo: '',
    userId: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.customerNo || !formData.userId) {
      setError('Vennligst fyll ut alle feltene');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await AuthService.forgotPassword(formData);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sjekk e-posten din</h2>
          <p className="text-gray-600 mb-6">
            Hvis brukeren finnes og har en registrert e-postadresse, vil du motta en e-post med instruksjoner for å tilbakestille passordet ditt.
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbake til innlogging
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Glemt passord?</h1>
          <p className="text-gray-600">
            Fyll inn informasjonen under, så sender vi deg en lenke for å tilbakestille passordet.
          </p>
        </div>

        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="customerNo" className="block text-sm font-medium text-gray-700 mb-2">
              Kundenummer
            </label>
            <input
              type="text"
              id="customerNo"
              name="customerNo"
              value={formData.customerNo}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="F.eks. 1001"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>

          <div>
            <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-2">
              Bruker-ID
            </label>
            <input
              type="text"
              id="userId"
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              disabled={isLoading}
              placeholder="Din bruker-ID"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>

          <Button
            onClick={handleSubmit}
            variant="primary"
            size="lg"
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sender...
              </>
            ) : (
              'Send tilbakestillingslenke'
            )}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Tilbake til innlogging
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
